/**
 * InfographicCard — renders the structured `infographic` object extracted by Claude
 * as a clean, branded visual card. No AI image generation needed.
 *
 * Data comes from judgment.infographic which Claude populates in the same call.
 */
import { Scale, Gavel, BookText, CheckCircle2, HelpCircle, Users } from "lucide-react";

function Pill({ children, variant = "left" }) {
  const cls = variant === "left"
    ? "bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800"
    : "bg-sky-100 text-sky-900 border-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800";
  return (
    <div className={`mb-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-widest ${cls}`}>
      {children}
    </div>
  );
}

function InfoRow({ icon: Icon, heading, text }) {
  if (!text) return null;
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-card/80 border shadow-sm">
        <Icon className="h-4 w-4 text-primary" />
      </span>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{heading}</p>
        <p className="mt-0.5 text-sm leading-relaxed text-foreground/90">{text}</p>
      </div>
    </div>
  );
}

export function InfographicCard({ infographic, caseTitle = "", citation = "", date = "" }) {
  if (!infographic) return null;

  const {
    headline_en, subtitle_en,
    central_question_en, appellant_argument_en, respondent_argument_en,
    verdict_points = [], concept_term_en, concept_definition_en,
    provisions_table = [],
  } = infographic;

  const hasTable = provisions_table.length > 0;
  const hasEquiv = provisions_table.some((r) => r?.equivalent?.trim());
  const metaLine = [citation, date].filter(Boolean).join("  ·  ") || "Supreme Court of India";

  return (
    <div className="overflow-hidden rounded-2xl border bg-[#F8F5F0] dark:bg-card text-foreground shadow-md" data-testid="infographic-card">
      {/* Header */}
      <div className="border-b bg-card/50 px-5 py-4 text-center">
        <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
          <Scale className="h-4 w-4" /> Litigation India Legal Explainer
        </div>
        {headline_en && (
          <h3 className="mt-2 font-serif text-lg font-bold leading-tight text-foreground sm:text-xl">
            {headline_en}
          </h3>
        )}
        {subtitle_en && (
          <p className="mt-1 text-sm text-muted-foreground">{subtitle_en}</p>
        )}
        {caseTitle && (
          <p className="mt-1.5 text-xs italic text-muted-foreground">{caseTitle}</p>
        )}
      </div>

      {/* Two-column body */}
      <div className="grid gap-px bg-border sm:grid-cols-2">
        {/* LEFT — The Legal Conflict */}
        <div className="space-y-4 bg-[#F8F5F0] dark:bg-card/50 p-5">
          <Pill variant="left"><Gavel className="h-3 w-3" /> The Legal Conflict</Pill>
          <InfoRow icon={HelpCircle} heading="The Central Legal Question" text={central_question_en} />
          <InfoRow icon={Users} heading="Appellant's Argument" text={appellant_argument_en} />
          {concept_term_en ? (
            <InfoRow icon={BookText} heading={`What is ${concept_term_en}?`} text={concept_definition_en} />
          ) : (
            <InfoRow icon={Users} heading="Respondent's Position" text={respondent_argument_en} />
          )}
        </div>

        {/* RIGHT — The Verdict */}
        <div className="space-y-4 bg-[#F0F4F8] dark:bg-card/60 p-5">
          <Pill variant="right"><Scale className="h-3 w-3" /> The Supreme Court's Verdict</Pill>
          {verdict_points.slice(0, 3).map((vp, i) => (
            vp?.title_en ? (
              <div key={i} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-card/80 border shadow-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                </span>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{vp.title_en}</p>
                  <p className="mt-0.5 text-sm leading-relaxed text-foreground/90">{vp.text_en}</p>
                </div>
              </div>
            ) : null
          ))}
        </div>
      </div>

      {/* Legal Reference Table */}
      {hasTable && (
        <div className="border-t bg-card/50 px-5 py-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">Legal Reference</p>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-sky-50 dark:bg-sky-950/30">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground">Legal Subject</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground">Provision Applied</th>
                  {hasEquiv && <th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground">Equivalent</th>}
                </tr>
              </thead>
              <tbody>
                {provisions_table.map((row, i) => row?.primary ? (
                  <tr key={i} className="border-t bg-card/60">
                    <td className="px-3 py-2 text-xs text-foreground/80">{row.subject || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-foreground">{row.primary}</td>
                    {hasEquiv && <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{row.equivalent || "—"}</td>}
                  </tr>
                ) : null)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between border-t bg-foreground/90 px-4 py-2 dark:bg-foreground/80">
        <span className="text-xs font-bold text-background">litigationindia.com</span>
        <span className="text-xs text-background/70">{metaLine}</span>
        <span className="text-xs font-bold text-background">Insight First, Action Next</span>
      </div>
    </div>
  );
}

export default InfographicCard;
